import Tipfy from "./tipfy";

const cache = new Map();

let tooltipsReady = false;

const shift = (year, month, step) => {
  const index = (year * 12 + (month - 1)) + step;

  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
};

export function initCalendar() {
  const root = document.querySelector("[data-calendar]");

  if (!root) {
    return;
  }

  const endpoint = root.dataset.calendarEndpoint;
  const busyBoxes = root.querySelectorAll("[data-calendar-busy]");
  const gridBox = root.querySelector("[data-calendar-grid]");
  const agendaBox = root.querySelector("[data-calendar-agenda]");
  const cardsBox = root.querySelector("[data-calendar-cards]");
  const labels = root.querySelectorAll("[data-calendar-label]");

  const setBusy = (busy) => {
    busyBoxes.forEach((box) => {
      box.dataset.calendarBusy = busy ? "true" : "false";
    });
  };
  const prev = root.querySelector("[data-calendar-prev]");
  const next = root.querySelector("[data-calendar-next]");

  if (!endpoint || !gridBox || !agendaBox) {
    return;
  }

  if (!tooltipsReady) {
    new Tipfy(".calendar-grid__event");
    tooltipsReady = true;
  }

  const state = {
    year: Number(root.dataset.calendarYear),
    month: Number(root.dataset.calendarMonth),
    category: root.dataset.calendarCategory || "",
  };

  const keyFor = (year, month, category) => `${year}-${month}-${category}`;

  const request = (year, month, category) => {
    const key = keyFor(year, month, category);

    if (cache.has(key)) {
      return cache.get(key);
    }

    const url = new URL(endpoint);
    url.searchParams.set("rok", year);
    url.searchParams.set("miesiac", month);

    if (category) {
      url.searchParams.set("kategoria", category);
    }

    const promise = fetch(url, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Nie udalo sie pobrac kalendarza");
        }

        return response.json();
      })
      .catch((error) => {
        cache.delete(key);
        throw error;
      });

    cache.set(key, promise);

    return promise;
  };

  const prefetch = (year, month, category) => {
    [-1, 1].forEach((step) => {
      const target = shift(year, month, step);
      request(target.year, target.month, category).catch(() => {});
    });
  };

  const updateArrows = () => {
    const back = shift(state.year, state.month, -1);
    const forward = shift(state.year, state.month, 1);

    if (prev) {
      prev.href = buildUrl(back.year, back.month, state.category);
    }

    if (next) {
      next.href = buildUrl(forward.year, forward.month, state.category);
    }

    root.querySelectorAll("[data-calendar-filter]").forEach((chip) => {
      chip.href = buildUrl(state.year, state.month, chip.dataset.calendarFilter);
    });
  };

  const buildUrl = (year, month, category) => {
    const url = new URL(window.location.href);
    url.searchParams.set("rok", year);
    url.searchParams.set("miesiac", month);

    if (category) {
      url.searchParams.set("kategoria", category);
    } else {
      url.searchParams.delete("kategoria");
    }

    return url.toString();
  };

  const paint = (data) => {
    gridBox.innerHTML = data.grid;
    agendaBox.innerHTML = data.agenda;

    if (cardsBox) {
      cardsBox.innerHTML = data.cards;
    }

    labels.forEach((label) => {
      label.textContent = data.label;
    });

    root.dataset.calendarYear = data.year;
    root.dataset.calendarMonth = data.month;
    root.dataset.calendarCategory = state.category;

    root.querySelectorAll("[data-calendar-filter]").forEach((chip) => {
      const active = (chip.dataset.calendarFilter || "") === state.category;
      chip.classList.toggle("is-active", active);

      if (active) {
        chip.setAttribute("aria-current", "true");
      } else {
        chip.removeAttribute("aria-current");
      }
    });

    updateArrows();
  };

  const go = (year, month, category, push = true) => {
    state.year = year;
    state.month = month;
    state.category = category;

    setBusy(true);

    request(year, month, category)
      .then((data) => {
        if (state.year !== year || state.month !== month || state.category !== category) {
          return;
        }

        paint(data);

        if (push) {
          window.history.pushState({ year, month, category }, "", buildUrl(year, month, category));
        }

        prefetch(year, month, category);
      })
      .catch(() => {
        window.location.href = buildUrl(year, month, category);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  root.addEventListener("click", (event) => {
    const link = event.target.closest("[data-calendar-prev], [data-calendar-next], [data-calendar-filter]");

    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
      return;
    }

    event.preventDefault();

    if (link.hasAttribute("data-calendar-filter")) {
      go(state.year, state.month, link.dataset.calendarFilter || "");
      return;
    }

    const step = link.hasAttribute("data-calendar-prev") ? -1 : 1;
    const target = shift(state.year, state.month, step);

    go(target.year, target.month, state.category);
  });

  window.addEventListener("popstate", (event) => {
    const data = event.state;

    if (data && data.year) {
      go(data.year, data.month, data.category || "", false);
    }
  });

  window.history.replaceState({ ...state }, "", window.location.href);
  prefetch(state.year, state.month, state.category);
}
